/**
 * GROQ queries for fetching workflow configuration from Sanity CMS.
 *
 * Two-tier fetching strategy:
 * - Manifest: lightweight, fetched once per session
 * - Step config: fetched per page, on demand
 */

// Manifest query — lightweight payload (~1-2KB)
// Returns step routing info (stepId, path) per category
export const WORKFLOW_MANIFEST_QUERY = `
  *[_type == "workflow" && variantId == $variantId][0] {
    _id,
    "id": _id,
    version,
    "sharedSteps": {
      "getStarted": sharedSteps.getStarted { stepId, path },
      "notServicedJunk": sharedSteps.notServicedJunk { title, description, primaryButton { text, url }, secondaryButton { text, url } },
      "notServicedMoving": sharedSteps.notServicedMoving { title, description, primaryButton { text, url }, secondaryButton { text, url } }
    },
    "serviceCategories": {
      "Junk": serviceCategories.Junk[] { stepId, path, tracks },
      "Moving": serviceCategories.Moving[] { stepId, path, tracks },
      "Labor": serviceCategories.Labor[] { stepId, path, tracks }
    }
  }
`;

// Field projection reused across contentBlocks[]
const FIELD_PROJECTION = `
  name,
  type,
  label,
  placeholder,
  description,
  required,
  defaultValue,
  options[] { value, label, icon, subLabel, ctaText },
  validation { type, message, value },
  gridSize { xs, sm, md },
  textBlocks { cantFindAddress, promoCodeRevealText, promoCodeInvalidText },
  skipValidation
`;

// Step config query — full payload per step
// Searches shared steps first, then only the active category's steps.
// $categorySlug narrows the search so each category can have different content
// for the same stepId (e.g. contact-information differs between Labor and Moving).
// When $categorySlug is null/omitted (e.g. get-started before category selection),
// coalesce returns [] and only sharedSteps are searched — which is correct.
export const STEP_CONFIG_QUERY = `
  *[_type == "workflow" && variantId == $variantId][0] {
    "step": [
      sharedSteps.getStarted,
      ...coalesce(serviceCategories[$categorySlug], [])
    ][stepId == $stepId][0] {
      stepId,
      title,
      titleVariant,
      titleAlign,
      subtitle,
      description,
      tip,
      tipIcon,
      hideTip,
      pageName,
      nextButtonText,
      customMaxWidth,
      tooltipHeaderText,
      getStartedContent,
      contentBlocks[] {
        _type == "fieldSchema" => {
          "blockType": "field",
          ${FIELD_PROJECTION}
        },
        _type == "componentContentBlock" => {
          "blockType": "component",
          componentKey,
          key,
          "columns": pricingColumns,
          "cards": pricingCards[] {
            "imageUrl": image.asset->url,
            name,
            shortName,
            priceTemplate
          },
          "textBlocks": {
            "arrivalWindowHelperText": arrivalWindowHelperText,
            "noAvailabilityMessage": noAvailabilityMessage,
            "callInsteadPromptText": callInsteadPromptText,
            "callInsteadActionText": callInsteadActionText,
            "pricingDescription1": pricingDescription1,
            "pricingDescription2": pricingDescription2,
            "backToMainSiteButton": backToMainSiteButton,
            "bookAnotherServiceButton": bookAnotherServiceButton,
            "requestSummaryTitle": requestSummaryTitle,
            "serviceAddressLabel": serviceAddressLabel,
            "phoneNumberLabel": phoneNumberLabel,
            "estimateDateLabel": estimateDateLabel,
            "arrivalWindowLabel": arrivalWindowLabel,
            "estimatedItemsLabel": estimatedItemsLabel,
            "confirmationNumberLabel": confirmationNumberLabel,
            "discountCodeLabel": discountCodeLabel
          },
          fields[] {
            ${FIELD_PROJECTION}
          }
        },
        _type == "dwellingInfoContentBlock" => {
          "blockType": "dwelling-info",
          key,
          source
        },
        _type == "addressDisplayContentBlock" => {
          "blockType": "address-display",
          key,
          source
        },
        _type == "smsConsentContentBlock" => {
          "blockType": "sms-consent",
          key,
          textBlocks {
            smsConsentPrefix,
            smsConsentLinkText,
            smsConsentSuffix,
            smsPolicyTitle,
            smsPolicyBody,
            smsPolicyCloseButton
          }
        },
        _type == "textContentBlock" => {
          "blockType": "text",
          key,
          content,
          variant,
          align,
          html
        },
        _type == "dividerContentBlock" => {
          "blockType": "divider",
          key
        },
        _type == "noteContentBlock" => {
          "blockType": "note",
          key,
          content,
          icon
        }
      }
    }
  }.step
`;
